// src/pages/Contact.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ContactForm from '../components/Forms/ContactForm';
import { MapContainer, Marker, TileLayer, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in react-leaflet - Place this right after imports
// @ts-ignore - _getIconUrl exists at runtime but is not declared in the Leaflet types
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Contact: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Contact Us - Impact Generation Africa</title>
        <meta 
          name="description" 
          content="Get in touch with Impact Generation Africa. We'd love to hear from you about partnerships, volunteering, or general inquiries." 
        />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="py-5 bg-success text-white"
        style={{ marginTop: '76px' }}
      >
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <motion.h1 
                className="display-5 fw-bold mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Contact Us
              </motion.h1>
              <motion.p 
                className="lead mb-0"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Get in touch with our team. We're here to help and answer any questions you might have.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <motion.div 
              className="col-lg-4 mb-4 mb-lg-0"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="fw-bold text-success mb-4">Get In Touch</h3>
              
              <div className="mb-4">
                <div className="d-flex align-items-start mb-3">
                  <div className="text-success me-3">
                    <i className="bi bi-geo-alt fs-4"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Our Office</h6>
                    <p className="text-muted mb-0">
                      Kapenguria West Pokot, Kenya<br />
                      P+254703 543 030
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                  <div className="text-success me-3">
                    <i className="bi bi-envelope fs-4"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Email Us</h6>
                    <p className="text-muted mb-0">
                      info@impactgenerationafrica.org<br />
                      support@impactgenerationafrica.org
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                  <div className="text-success me-3">
                    <i className="bi bi-telephone fs-4"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Call Us</h6>
                    <p className="text-muted mb-0">
                      +254 703 543 030<br />
                      +44 7763 638768
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start">
                  <div className="text-success me-3">
                    <i className="bi bi-clock fs-4"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Office Hours</h6>
                    <p className="text-muted mb-0">
                      Monday - Friday: 8:00 AM - 5:00 PM<br />
                      Saturday: 9:00 AM - 1:00 PM
                    </p>
                  </div>
                </div>
              </div>

              <div className="social-links">
                <h6 className="fw-bold mb-3">Follow Us</h6>
                <div className="d-flex gap-3">
                  <a href="https://web.facebook.com/profile.php?id=100068962459906" className="text-success fs-4">
                    <i className="bi bi-facebook"></i>
                  </a>
                  <a href="#" className="text-success fs-4">
                    <i className="bi bi-twitter"></i>
                  </a>
                  <a href="#" className="text-success fs-4">
                    <i className="bi bi-instagram"></i>
                  </a>
                  <a href="#" className="text-success fs-4">
                    <i className="bi bi-linkedin"></i>
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="col-lg-8"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="fw-bold text-success text-center mb-4">Find Us</h3>
                <div className="bg-white rounded shadow-sm p-4">
                  <MapContainer 
                    center={[1.2308, 35.1175]} 
                    zoom={13} 
                    style={{ height: '400px', width: '100%', borderRadius: '8px' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[1.2308, 35.1175]}>
                      <Popup>
                        <strong>Head Office</strong><br />
                        Location: 1.2308° N, 35.1175° E
                      </Popup>
                    </Marker>
                  </MapContainer>
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      <i className="bi bi-geo-alt-fill me-1"></i>
                      Head Office: 1.2308° N, 35.1175° E
                    </small>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;